import styles from './styles.module.scss';
import { Modal } from 'components-antd';
import { ModalProps } from 'components-antd';
import { Spinner } from 'components';
import { CloseModalIconSmall } from 'pages/ServicesCategory/components/AddVendorModal/icons';
import classNames from 'classnames';
import moment from 'moment';
import { getDateOnly } from 'helpers';
import { isBeforeToday, isToday, isYesterday } from 'helpers/date';
import { Table } from 'components-antd';
import { mapTasksStatuses, tasksStatusesIds } from 'settings/constants/transactionTasks';
import { Icons } from 'pages/Workshop/Icons';
import { useCallback } from 'react';

export interface TasksModalPropsType extends ModalProps {
  loading: boolean;
  handleClose: () => void;
  tasks: any[];
  handleTaskViewClick: (row) => void;
}

const TasksModal = (props: TasksModalPropsType) => {
  const { open, handleClose, loading, tasks, handleTaskViewClick } = props;

  const handleModalClose = () => {
    handleClose();
  };

  const getStatusClassName = useCallback((val) => {
    switch (val) {
      case tasksStatusesIds.new:
        return styles.new;
      case tasksStatusesIds.inProgress:
        return styles.inProgress;
      case tasksStatusesIds.stuck:
        return styles.stuck;
      case tasksStatusesIds.done:
        return styles.done;
      case tasksStatusesIds.overdue:
        return styles.overdue;
      case tasksStatusesIds.na:
        return styles.na;
      default:
        return '';
    }
  }, []);

  const boardIcon = (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8 5.00022H6.94444C6.42875 5.00022 5.93417 5.20508 5.56951 5.56973C5.20486 5.93439 5 6.42896 5 6.94466V18.0558C5 18.5715 5.20486 19.066 5.56951 19.4307C5.93417 19.7954 6.42875 20.0002 6.94444 20.0002L17.0556 20C17.5713 20 18.0658 19.7951 18.4305 19.4305C18.7951 19.0658 19 18.5713 19 18.0556V6.94444C19 6.42875 18.7951 5.93417 18.4305 5.56951C18.0658 5.20486 17.5713 5 17.0556 5H16"
        stroke="#515151"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13.5 3H10.5C9.39543 3 8.5 3.89543 8.5 5C8.5 6.10457 9.39543 7 10.5 7H13.5C14.6046 7 15.5 6.10457 15.5 5C15.5 3.89543 14.6046 3 13.5 3Z"
        stroke="#515151"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9.5 11H14.5"
        stroke="#515151"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9.5 15H14.5"
        stroke="#515151"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const columns: any[] = [
    {
      title: 'Task',
      key: 'task',
      dataIndex: 'Title',
      width: 500,
      sorter: (a, b) => {
        if (a.Title < b.Title) return -1;
        if (a.Title > b.Title) return 1;
        return 0;
      },
      render: (_, row) => {
        const getSecondLineText = () => {
          const { IsProject, Name, Property } = row?.Transaction ?? {};
          return IsProject ? Name : Property?.Address?.Line1;
        };

        return (
          <div className={styles.taskTitleContainer}>
            <div className={styles.icon}>
              {row?.TransactionId ? <Icons variant={Icons.OFFER} /> : boardIcon}
            </div>
            <div className={styles.info}>
              <div className={styles.infoTitle}>{row.Title}</div>
              {getSecondLineText() && (
                <div className={styles.infoSubTitle}>{getSecondLineText()}</div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      dataIndex: 'Status',
      width: 170,
      sorter: (a, b) => {
        if (a.Status < b.Status) return -1;
        if (a.Status > b.Status) return 1;
        return 0;
      },
      render: (_, { Status }) => (
        <div
          className={classNames(
            styles.taskStatus,
            Status === tasksStatusesIds.inProgress ? styles.taskStatusYellow : styles.taskStatusRed,
            getStatusClassName(Status),
          )}
        >
          {mapTasksStatuses[Status]}
        </div>
      ),
    },
    {
      title: 'Due Date',
      key: 'dueDate',
      dataIndex: 'DueDate',
      width: 148,
      sorter: (a, b) => moment(a.DueDate).unix() - moment(b.DueDate).unix(),
      render: (_, { DueDate }) => {
        let dueDate = DueDate ? getDateOnly(DueDate, 'M/D/YYYY') : 'N/A';
        let isDatePassed = false;

        if (isToday(dueDate)) dueDate = 'Today';
        if (isYesterday(dueDate)) {
          dueDate = 'Yesterday';
          isDatePassed = true;
        }
        if (isBeforeToday(dueDate)) isDatePassed = true;

        return (
          <div
            className={classNames(styles.taskDate, isDatePassed ? styles.taskDateRed : undefined)}
          >
            {dueDate}
          </div>
        );
      },
    },
    {
      title: '',
      key: 'view',
      width: 150,
      render: (_, row) => {
        return (
          <div className={styles.taskView}>
            <div className={styles.taskViewButton} onClick={() => handleTaskViewClick(row)}>
              View
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <Modal
      className={styles.tasksModal}
      footer={null}
      width={1002}
      open={open}
      onCancel={handleModalClose}
      destroyOnClose
      closable={true}
      zIndex={1000}
    >
      <div className={styles.content}>
        <div className={styles.header}>
          <div></div>
          <div>Tasks to Complete</div>
          <div className={styles.close} onClick={handleModalClose}>
            <CloseModalIconSmall />
          </div>
        </div>
        {loading ? (
          <div className={classNames(styles.tasksTable, styles.otherStates)}>
            <Spinner />
          </div>
        ) : tasks.length ? (
          <div className={styles.tasksTableContainer}>
            <Table
              className={styles.tasksTable}
              loading={loading}
              bordered={false}
              columns={columns}
              dataSource={tasks.map((item, key) => ({ ...item, key }))}
              pagination={false}
            />
          </div>
        ) : (
          <div className={classNames(styles.tasksTable, styles.otherStates)}>No Tasks.</div>
        )}
      </div>
    </Modal>
  );
};

export default TasksModal;
